/** Prefix dangerous spreadsheet values only on export, never alter imported identities. */
export function safeCsvCell(value: unknown): string {
  const text = String(value ?? '');
  const safe = /^[\s\u0000-\u001f]*[=+@-]/.test(text) || /^[\t\r\n]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

/**
 * The template's columns, in the order the owner asked for (26 Oct 2026):
 * Employee ID, name, mobile, gender, date of birth, then blood group. The
 * columns after the first five are optional; the person downloading picks
 * which ones they want before the file is made. The server reads columns by
 * name, so a file with fewer, more or reordered columns still imports.
 */
export interface TemplateColumn { key: string; header: string; example: string; optional: boolean; defaultOn: boolean }
export const TEMPLATE_COLUMNS: readonly TemplateColumn[] = [
  { key: 'employeeCode', header: 'Employee ID', example: 'EMP-1001', optional: false, defaultOn: true },
  { key: 'fullName', header: 'Full Name', example: 'Anita Rao', optional: false, defaultOn: true },
  { key: 'mobile', header: 'Mobile Number', example: '9812340001', optional: false, defaultOn: true },
  { key: 'sex', header: 'Gender', example: 'FEMALE', optional: false, defaultOn: true },
  { key: 'dateOfBirth', header: 'Date of Birth', example: '1992-03-15', optional: false, defaultOn: true },
  { key: 'bloodGroup', header: 'Blood Group', example: 'B+', optional: true, defaultOn: true },
  { key: 'email', header: 'Email', example: 'anita@example.com', optional: true, defaultOn: false },
  { key: 'employmentType', header: 'Employment Type', example: 'DIRECT', optional: true, defaultOn: false },
  { key: 'joinedOn', header: 'Joining Date', example: '2024-06-01', optional: true, defaultOn: false },
  // Optional details a company often has to hand (owner, 29 Oct 2026).
  { key: 'designation', header: 'Designation', example: 'Machine operator', optional: true, defaultOn: false },
  { key: 'departmentCode', header: 'Department', example: 'Paint shop', optional: true, defaultOn: false },
  { key: 'natureOfWork', header: 'Nature of work', example: 'Welding, work at height', optional: true, defaultOn: false },
];

/** The columns a template is made with: the required ones, then the chosen optional ones. */
export function templateColumns(optionalChosen: ReadonlySet<string>): TemplateColumn[] {
  return TEMPLATE_COLUMNS.filter((c) => !c.optional || optionalChosen.has(c.key));
}

export const defaultOptionalColumns = (): Set<string> =>
  new Set(TEMPLATE_COLUMNS.filter((c) => c.optional && c.defaultOn).map((c) => c.key));

/** Every column, for callers that want the whole default template. */
export const employeeTemplateHeaders = templateColumns(defaultOptionalColumns()).map((c) => c.header);
export const employeeTemplateExample = templateColumns(defaultOptionalColumns()).map((c) => c.example);

export function rowsAsCsv(rows: unknown[][]) { return '﻿' + rows.map(r => r.map(safeCsvCell).join(',')).join('\r\n'); }
export function friendlyImportError(field: string, message: string) {
  const labels: Record<string, string> = { employee_code: 'Employee ID', full_name: 'Full name', date_of_birth: 'Date of birth', sex: 'Gender', mobile: 'Mobile', plant_id: 'Plant', plant_code: 'Plant', employment_type: 'Employment type', joining_date: 'Joining date', blood_group: 'Blood group', designation: 'Designation', department_code: 'Department', nature_of_work: 'Nature of work' };
  return message.replace(new RegExp(`^${field}\\s*`), `${labels[field] ?? field.replaceAll('_', ' ')} `);
}
